import { useCallback, useState } from "react"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"
import {
  useUpdateCampaignBudgetMutation,
  useUpdateAdSetBudgetMutation,
  useUpdateCampaignStatusMutation,
  useUpdateAdSetStatusMutation,
  usePreviewGoogleAdsChangeMutation,
  useConfirmGoogleAdsChangeMutation,
} from "~/ui-rtk/api/paidMediaApi"
import {
  useCreateAuditMutation,
  type BudgetRecommendationAuditItem,
} from "~/ui-rtk/api/auditsApi"
import {
  BudgetType,
  CampaignStatus,
  AdSetStatus,
} from "~/ui-rtk/api/types/paid-media.types"

import type {
  RecommendationRow,
  ApplyResult,
  BudgetInputs,
  TroasChange,
} from "../types"
import { describeRecentChange, validateDailyBudget } from "../budget-utils"

/**
 * What the batch audit records for a row that carried a recent-change label
 * when it was approved: the newest change (reason, when, by whom, the audit
 * that proves it) and a one-line description, so the run history shows the
 * row was changed knowingly. Empty for an unlabeled row.
 */
export function recentChangeAuditFields(
  row: Pick<RecommendationRow, "recentChanges">,
  /** false for a row left as is: the label rides along, but nothing was overridden. */
  overrode = true,
): Pick<
  BudgetRecommendationAuditItem,
  | "overrodeRecentChange"
  | "recentChangeReason"
  | "recentChangeAt"
  | "recentChangeAuditId"
  | "recentChangeActor"
  | "recentChangeNote"
> {
  const changes = row.recentChanges ?? []
  const change = changes[0]
  if (!change) return {}
  return {
    ...(overrode ? { overrodeRecentChange: true as const } : {}),
    recentChangeReason: change.reason,
    recentChangeAt: change.at,
    recentChangeAuditId: change.auditId,
    recentChangeActor: change.actor,
    recentChangeNote: changes.map(describeRecentChange).join("; "),
  }
}

/**
 * Marks a row the initiative-type gate flagged for untyped spending ad sets
 * (an active, typed campaign some of whose ad sets have no type). Such a
 * row only reaches an approved batch when the user overrode the gate, so
 * the marker is the run history's record of that override; rows with no
 * type at all carry `skipReason: "no-initiative-type"` instead.
 */
export function initiativeTypeGateAuditFields(
  row: Pick<RecommendationRow, "isActive" | "hasUncategorizedAdsets">,
): Pick<BudgetRecommendationAuditItem, "untypedAdsets"> {
  return row.isActive && row.hasUncategorizedAdsets
    ? { untypedAdsets: true }
    : {}
}

export function useBulkBudgetApprove() {
  const [updateCampaignBudget] = useUpdateCampaignBudgetMutation()
  const [updateAdSetBudget] = useUpdateAdSetBudgetMutation()
  const [updateCampaignStatus] = useUpdateCampaignStatusMutation()
  const [updateAdSetStatus] = useUpdateAdSetStatusMutation()
  const [previewGoogleAdsChange] = usePreviewGoogleAdsChangeMutation()
  const [confirmGoogleAdsChange] = useConfirmGoogleAdsChangeMutation()
  const [createAudit] = useCreateAuditMutation()
  const [isApproving, setIsApproving] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })

  const approve = useCallback(
    async (
      rows: RecommendationRow[],
      editOverrides: Map<string, number>,
      inputs?: BudgetInputs,
      /**
       * Smart-scale companion moves for target-constrained Google campaigns
       * (keyed by campaignId). Applied AFTER the budget write succeeds, via
       * the gated preview→confirm pair — the server re-runs every guardrail
       * (cap, floor, cooldown, learning) even though the suggestion was
       * pre-clamped, so a stale suggestion can never slip through.
       */
      smartScale?: {
        companyId: string
        troasChanges: Map<string, TroasChange>
      },
      /**
       * Active rows deliberately left at their current budget (unchecked, or
       * labeled and left out). Never written; recorded in the batch audit as
       * skipped so the run history shows the WHOLE pool the recommender
       * allocated against, not just what it touched.
       */
      unchangedRows: RecommendationRow[] = [],
    ): Promise<ApplyResult[]> => {
      // Only apply rows where we can actually set budgets via API
      const applyableRows = rows.filter(r => r.canApplyBudget)

      setIsApproving(true)
      setProgress({ done: 0, total: applyableRows.length })

      const results: ApplyResult[] = []
      // Full-context rows for the ONE `budget-recommendation` audit written
      // after the batch — the history record of this bulk action. The
      // per-entity budget calls pass `skipRecord` so no `budget-update`
      // audit rows are written alongside it.
      const auditItems: BudgetRecommendationAuditItem[] = []

      for (let i = 0; i < applyableRows.length; i++) {
        const row = applyableRows[i]
        const budgetDollars =
          editOverrides.get(row.entityId) ?? row.adjustedBudget
        const budgetCents = Math.round(budgetDollars * 100)
        const platform = row.platform

        if (!platform) {
          results.push({
            entityId: row.entityId,
            entityName: row.entityName,
            channel: row.channel,
            success: false,
            error: "Unsupported platform",
          })
          setProgress({ done: i + 1, total: applyableRows.length })
          continue
        }

        // A row with a recent manual change (or first spend) is written like
        // any other: the user acknowledged the label before reaching the
        // review. The batch audit records what was overridden so the run
        // history shows the change was made knowingly. Same for a row the
        // initiative-type gate flagged and the user approved past.
        const overrideFields = {
          ...recentChangeAuditFields(row),
          ...initiativeTypeGateAuditFields(row),
        }

        // Final safety gate for budget changes (pause is a valid action on its
        // own). Catches missing-data $0 recommendations and any fat-finger /
        // non-finite value that slipped past the input control, so we never
        // write a nonsensical budget. Defense-in-depth with InlineEditCell.
        if (!row.shouldPause) {
          const budgetError = validateDailyBudget(budgetDollars)
          if (budgetError) {
            results.push({
              entityId: row.entityId,
              entityName: row.entityName,
              channel: row.channel,
              success: false,
              error: row.hasMissingData
                ? "Skipped: incomplete performance data — set a budget manually to apply"
                : `Skipped: ${budgetError} (got $${budgetDollars})`,
            })
            setProgress({ done: i + 1, total: applyableRows.length })
            continue
          }
        }

        let success = true
        let error: string | undefined
        try {
          if (row.shouldPause) {
            // Pause the entity instead of updating budget
            if (row.entityType === "campaign") {
              await updateCampaignStatus({
                platform,
                campaignId: row.entityId,
                data: { status: CampaignStatus.PAUSED },
              }).unwrap()
            } else {
              await updateAdSetStatus({
                platform,
                adsetId: row.entityId,
                data: { status: AdSetStatus.PAUSED },
              }).unwrap()
            }
          } else if (row.entityType === "campaign") {
            await updateCampaignBudget({
              platform,
              campaignId: row.entityId,
              data: {
                budgetType: BudgetType.DAILY,
                budgetAmount: budgetCents,
              },
              skipRecord: true,
            }).unwrap()
          } else {
            await updateAdSetBudget({
              platform,
              adsetId: row.entityId,
              data: {
                dailyBudget: budgetCents,
              },
              skipRecord: true,
            }).unwrap()
          }
          // Combined smart-scale: the budget landed; now move the tROAS that
          // was actually limiting the campaign. Runs through preview→confirm
          // so the server-side guardrails re-validate against live state. A
          // failure here is a PARTIAL success — the budget stands, the row
          // says exactly which half failed.
          const troasChange =
            !row.shouldPause &&
            row.entityType === "campaign" &&
            platform === "google"
              ? smartScale?.troasChanges.get(row.campaignId)
              : undefined
          let troasApplied: boolean | undefined
          let troasError: string | undefined
          if (troasChange && smartScale) {
            try {
              const preview = await previewGoogleAdsChange({
                company: smartScale.companyId,
                changeType: "bid-target",
                entityId: row.entityId,
                targetRoas: troasChange.toTargetRoas,
                // The recommender's own move: never a hold against itself.
                initiator: "budget-recommender",
              }).unwrap()
              await confirmGoogleAdsChange({
                company: smartScale.companyId,
                token: preview.token,
              }).unwrap()
              troasApplied = true
            } catch (err) {
              troasApplied = false
              // .unwrap() rejects with a plain {status, data} object, never an
              // Error, so instanceof alone always fell to the generic text and
              // discarded the server's actual guardrail message (cooldown,
              // 20% cap, DR-ROAS floor) sitting at err.data.message.
              troasError = getServerErrorMessage(
                err,
                err instanceof Error ? err.message : "tROAS change failed",
              )
            }
          }

          results.push({
            entityId: row.entityId,
            entityName: row.entityName,
            channel: row.channel,
            success: true,
            paused: row.shouldPause,
            ...(troasApplied !== undefined ? { troasApplied } : {}),
            ...(troasError !== undefined ? { troasError } : {}),
          })

          auditItems.push({
            platform,
            entity: row.entityType,
            entityId: row.entityId,
            entityName: row.entityName,
            campaignId: row.campaignId,
            initiativeType: row.initiativeType,
            channel: row.channel,
            previousDailyBudgetCents: Math.round(row.currentDailyBudget * 100),
            ...(row.shouldPause
              ? { action: "pause" as const }
              : {
                  action: "update" as const,
                  newDailyBudgetCents: budgetCents,
                }),
            ...(troasChange
              ? {
                  previousTargetRoas: troasChange.fromTargetRoas,
                  newTargetRoas: troasChange.toTargetRoas,
                  troasSource: troasChange.source,
                  troasApplied,
                  ...(troasError !== undefined ? { troasError } : {}),
                }
              : {}),
            ...overrideFields,
            success: true,
          })

          setProgress({ done: i + 1, total: applyableRows.length })
          continue
        } catch (err) {
          success = false
          // Same {status, data} shape as the tROAS catch above.
          error = getServerErrorMessage(
            err,
            err instanceof Error ? err.message : "Update failed",
          )
          results.push({
            entityId: row.entityId,
            entityName: row.entityName,
            channel: row.channel,
            success: false,
            error,
          })
        }

        auditItems.push({
          platform,
          entity: row.entityType,
          entityId: row.entityId,
          entityName: row.entityName,
          campaignId: row.campaignId,
          initiativeType: row.initiativeType,
          channel: row.channel,
          previousDailyBudgetCents: Math.round(row.currentDailyBudget * 100),
          ...(row.shouldPause
            ? { action: "pause" as const }
            : { action: "update" as const, newDailyBudgetCents: budgetCents }),
          // The recommendation context this row was decided on. Per-row, not
          // per-batch — each row's analysis window is anchored separately
          // (Google Demand Gen lags), so this cannot fold into `context`.
          confidence: row.confidence,
          analysisPeriodDays: row.analysisPeriodDays,
          dataThroughDate: row.dataThroughDate,
          drRoas: row.drRoas,
          brandRoas: row.brandRoas,
          ...overrideFields,
          success,
          error,
        })

        setProgress({ done: i + 1, total: applyableRows.length })
      }

      // The rows left as is: never written, but part of the record. The
      // history report and the evaluation doctrine want to see the whole pool
      // the batch was allocated against, including what was NOT touched and
      // why ("not selected", a recent change the user chose not to
      // override, or no initiative type — only when the user overrode the
      // type gate, so the record shows the batch went through without one).
      for (const row of unchangedRows) {
        if (!row.platform) continue
        auditItems.push({
          platform: row.platform,
          entity: row.entityType,
          entityId: row.entityId,
          entityName: row.entityName,
          campaignId: row.campaignId,
          initiativeType: row.initiativeType,
          channel: row.channel,
          previousDailyBudgetCents: Math.round(row.currentDailyBudget * 100),
          action: "skip",
          skipReason:
            row.category === null ? "no-initiative-type" : "not-selected",
          // The label the row carried, without the override flag: the user
          // left it alone rather than changing it despite the label.
          ...recentChangeAuditFields(row, false),
          ...initiativeTypeGateAuditFields(row),
          confidence: row.confidence,
          analysisPeriodDays: row.analysisPeriodDays,
          dataThroughDate: row.dataThroughDate,
          drRoas: row.drRoas,
          brandRoas: row.brandRoas,
          success: true,
        })
      }

      // Best-effort: record the whole batch as ONE audit — which entities
      // changed, previous → new budgets, campaigns, initiative types, and
      // which rows failed. The budget changes above already happened; never
      // fail the approve on record-keeping.
      if (auditItems.length > 0) {
        try {
          await createAudit({
            type: "budget-recommendation",
            context: inputs
              ? {
                  targetDailyBudget: inputs.targetDailyBudget,
                  targetDRRoas: inputs.targetDRRoas,
                  targetBrandRoas: inputs.targetBrandRoas,
                }
              : undefined,
            items: auditItems,
          }).unwrap()
        } catch {
          // Audit write only — the applied changes stand regardless.
        }
      }

      setIsApproving(false)
      return results
    },
    [
      updateCampaignBudget,
      updateAdSetBudget,
      updateCampaignStatus,
      updateAdSetStatus,
      previewGoogleAdsChange,
      confirmGoogleAdsChange,
      createAudit,
    ],
  )

  return { approve, isApproving, progress }
}
